import React, { useMemo, useEffect, useState } from "react";
import { getProjectApprovalList } from "../../api/project";
import { toast } from "react-toastify";
import useDebounce from "../../hooks/useDebounce";
import { Link, useNavigate } from "react-router-dom";
import { checkPermissions, formatDate } from "../../utils/helper";
import UpArrow2 from "../../assets/svg-tsx/UpArrow2";
import EditIcon from "../../assets/EditIcon";
import SearchIcon from "../../assets/SearchIcon";
import InputField from "../../components/InputField";
import Pagination from "../../components/Pagination";
import ManagementTableLayout from "../../components/MangementTable";
import { useTable, usePagination, useSortBy } from "react-table";
import { Tooltip } from "antd";
import { useRecoilValue } from "recoil";
import { userInfoAtom } from "../../atom";

const ApprovalList = () => {
  const userInfo = useRecoilValue(userInfoAtom);

  const [totalCount, setTotalCount] = useState<number>(0);
  const [pageLength, setPageLength] = useState<number>(25);
  const [page, setPage] = useState(1);
  const [loading, setLoading] = useState(false);
  const [inputVal, setInputVal] = useState("");
  const navigate = useNavigate();
  const [settableData, setSettableData] = useState<any>([]);
  const [startIndex, setStartIndex] = useState(0);

  const debouncedInputVal = useDebounce(inputVal, 500);
  const [sortBy, setSortBy] = useState("");

  //Permissions
  const hasEditPermission = checkPermissions("edit_project");
  const hasViewPermission = checkPermissions("view_project", true, toast);

  useEffect(() => {
    setLoading(true);
    getProjectApprovalList({
      page: page,
      length: pageLength,
      search: debouncedInputVal,
      sort_by: sortBy,
    })
      .then((response) => {
        const projects = response.data.projects;
        setSettableData(projects);
        setTotalCount(response.data.pagination.total_count);
        setLoading(false);
      })
      .catch((error) => {
        console.error("Error fetching projects:", error);
        setLoading(false);
      });
  }, [page, pageLength, debouncedInputVal, sortBy]);

  const handleSort = (field: string) => {
    if (sortBy === field) {
      // If already sorting by this field, toggle to descending
      setSortBy(`-${field}`);
    } else if (sortBy === `-${field}`) {
      // If already sorting by this field descending, clear sort
      setSortBy("");
    } else {
      // Otherwise, sort by this field ascending
      setSortBy(field);
    }
  };

  // Helper function to determine sort direction for a field
  const getSortDirection = (field: string) => {
    if (sortBy === field) return "asc";
    if (sortBy === `-${field}`) return "desc";
    return "none";
  };

  const COLUMN = [
    {
      id: "name",
      Header: ({ column }: { column: any }) => <div>Project name</div>,
      accessor: "name",
      width: "20%",
      Cell: ({ value, row }: any) => (
        <div className="underline underline-offset-2">
          <Link to={`/projects/${row.original.id}`}>{value}</Link>
        </div>
      ),
    },
    {
      id: "customer_name",
      Header: ({ column }: { column: any }) => <div>Customer Name</div>,
      accessor: "customer_name",
      width: "20%",
      Cell: ({ value }: any) => (
        <div className="break-words">{value || "-"}</div>
      ),
    },
    {
      id: "status",
      Header: ({ column }: { column: any }) => <div>Status</div>,
      accessor: "status",
      width: "15%",
      Cell: ({ value }: any) => (
        <p
          className={`w-[max-content] m-1.5 py-0.5 px-2 rounded-md ${
            value === "CHANGE_REQUESTED"
              ? "bg-[#BFB2FF]"
              : value === "IN_REVIEW"
              ? "bg-[#FFC5EF]"
              : value === "REJECTED"
              ? "bg-[#FFB4A1]"
              : value === "APPROVED"
              ? "bg-[#B6FDC6]"
              : ""
          }`}
        >
          {value?.replace(/_/g, " ")}
        </p>
      ),
    },
    {
      id: "requested_date",
      Header: ({ column }: { column: any }) => (
        <div
          className="flex justify-center items-center gap-2 cursor-pointer"
          onClick={() => handleSort("requested_date")}
        >
          <p>Requested Date</p>
          <div className="w-4 h-4 flex items-center justify-center">
            {/* <UpArrow2
              className={`h-4 w-4 transition-transform ${
                getSortDirection("requested_date") === "desc"
                  ? "rotate-180"
                  : getSortDirection("requested_date") === "none"
                  ? "opacity-50"
                  : ""
              }`}
            /> */}
          </div>
        </div>
      ),
      width: "15%",
      accessor: "requested_date",
      Cell: ({ value }: any) => <div>{formatDate(value)}</div>,
    },

    ...(hasEditPermission
      ? [
          {
            id: "action",
            width: "15%",
            Header: ({ column }: { column: any }) => (
              <div className="flex justify-center items-center w-full">
                <p className="text-center">Action Items</p>
              </div>
            ),
            accessor: "",
            Cell: ({ row }: any) => (
              <div className="flex gap-x-2 px-2 items-center justify-center">
                {hasEditPermission && (
                  <Tooltip title="Edit">
                    <Link to={`/projects/${row.original.id}`}>
                      <div className="">
                        <EditIcon color="black" />
                      </div>
                    </Link>
                  </Tooltip>
                )}
              </div>
            ),
          },
        ]
      : []),
  ];

  useEffect(() => {
    const lastIndex = (page - 1) * pageLength;
    setStartIndex(lastIndex);
  }, [page, pageLength, totalCount]);

  const columns = useMemo(() => COLUMN, [settableData]);
  const data = useMemo(() => settableData || [], [settableData]);

  const tableInstance = useTable(
    {
      columns,
      data,
      initialState: { pageIndex: page, pageSize: pageLength },
      manualPagination: true,
      pageCount: Math.ceil(totalCount / pageLength),
    },
    useSortBy,
    usePagination
  );

  const handlePageChange = (page: any) => {
    setPage(page);
  };

  const handlePageSizeChange = (size: any) => {
    setPageLength(size);
  };

  if (!hasViewPermission) {
    navigate("/unauthorized");
  }

  if (userInfo?.channel_partner) {
    navigate("/unauthorized");
  }

  return (
    <div className="h-full">
      <div className="flex flex-row justify-between items-center py-2 w-full px-3 h-[3rem]">
        <div className="flex gap-4 w-[max-content]">
          <div className="w-[235px]">
            <InputField
              onChange={(e: string) => {
                setInputVal(e);
                if (page !== 1) {
                  setPage(1);
                }
              }}
              placeholder="Search"
              value={inputVal}
              rightIcon={<SearchIcon />}
            />
          </div>
        </div>
      </div>

      <div className="h-[calc(100%-6rem)] w-full relative -z-0  mt-2">
        <ManagementTableLayout
          tableInstance={tableInstance}
          loading={loading}
        />
      </div>

      {loading ? (
        ""
      ) : (
        <div className="w-full">
          <Pagination
            currentPage={page}
            setCurrentPage={setPage}
            pageSize={pageLength}
            totalCount={totalCount}
            onPageChange={handlePageChange}
            onPageSizeChange={handlePageSizeChange}
          />
        </div>
      )}
    </div>
  );
};
export default ApprovalList;
